import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, PhoneOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import EChart from '../../components/EChart';
import { track } from '../../api/analytics';
import {
  createLead,
  createShare,
  downloadPdf,
  GRADE_COLORS,
  getReport,
  PRIORITY_LABEL,
  ReportData,
  saveBlob,
} from '../../api/report';

/**
 * 诊断报告页（M6 核心）
 * - 总分 + 等级徽章、雷达图、维度柱状、等级仪表
 * - 核心发现（优势/薄弱 + 反向题交叉印证）
 * - 改进建议（按评分引擎排序）
 * - 预约顾问留资 + 免责声明
 */

export default function ReportPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [leadOpen, setLeadOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [leadForm] = Form.useForm();

  useEffect(() => {
    getReport(id)
      .then((d) => { setData(d); track('report_view', { diagnosisId: id }); })
      .catch((err: unknown) => {
        const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string };
        let msg = e?.response?.data?.message ?? e?.message ?? '报告加载失败，请稍后重试';
        if (e?.response?.status === 404) msg = '诊断不存在或链接已失效';
        else if (e?.response?.status === 401) msg = '请先登录后再查看';
        message.error(msg);
        console.error('[Report] load failed', err);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const radarOption = useMemo(() => {
    if (!data) return {};
    const dims = data.score.dimensionDetails;
    return {
      tooltip: {},
      radar: {
        indicator: dims.map((d) => ({ name: d.name, max: 100 })),
        radius: '65%',
      },
      series: [
        {
          type: 'radar',
          data: [
            {
              value: dims.map((d) => d.score ?? 0),
              name: '健康度',
              areaStyle: { color: 'rgba(22,119,255,0.2)' },
              lineStyle: { color: '#1677ff' },
            },
          ],
        },
      ],
    };
  }, [data]);

  const barOption = useMemo(() => {
    if (!data) return {};
    const dims = data.score.dimensionDetails;
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 30, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category', data: dims.map((d) => d.name) },
      yAxis: { type: 'value', max: 100 },
      series: [
        {
          type: 'bar',
          data: dims.map((d) => d.score ?? 0),
          itemStyle: {
            color: (p: { dataIndex: number }) =>
              GRADE_COLORS[dims[p.dataIndex].score === null ? 'E' : gradeOf(dims[p.dataIndex].score ?? 0)],
          },
          barWidth: 40,
        },
      ],
    };
  }, [data]);

  const gaugeOption = useMemo(() => {
    if (!data) return {};
    return {
      series: [
        {
          type: 'gauge',
          startAngle: 210,
          endAngle: -30,
          min: 0,
          max: 100,
          progress: { show: true, width: 18, itemStyle: { color: GRADE_COLORS[data.score.grade] ?? '#1677ff' } },
          axisLine: { lineStyle: { width: 18, color: [[1, '#f0f0f0']] } },
          axisTick: { show: false },
          splitLine: { length: 12 },
          axisLabel: { distance: 20 },
          pointer: { width: 4 },
          detail: {
            valueAnimation: true,
            formatter: '{value}',
            fontSize: 34,
            offsetCenter: [0, '70%'],
          },
          data: [{ value: data.score.totalScore }],
        },
      ],
    };
  }, [data]);

  const highlights = useMemo(() => {
    if (!data) return { strengths: [], weaknesses: [] };
    const dims = data.score.dimensionDetails.filter(
      (d): d is (typeof data.score.dimensionDetails)[number] & { score: number } => d.score !== null,
    );
    const sorted = [...dims].sort((a, b) => b.score - a.score);
    return {
      strengths: sorted.slice(0, 2),
      weaknesses: sorted.slice(-2).reverse(),
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: 15, color: '#595959' }}>
          🚀 正在生成报告与 AI 建议…
        </div>
        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
          首次生成约需 10-20 秒，请稍候（再次打开将秒开）
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <Card>
        <Alert
          type="warning"
          showIcon
          message="无法加载报告"
          description="可能是诊断不存在、分享链接已过期，或网络异常。请确认链接正确后重试。"
          action={
            <Space>
              <Button onClick={() => window.location.reload()}>重新加载</Button>
              <Button type="primary" onClick={() => navigate('/')}>去首页</Button>
            </Space>
          }
        />
      </Card>
    );
  }

  const { diagnosis, score } = data;

  const submitLead = async () => {
    const values = await leadForm.validateFields();
    await createLead({
      name: values.name,
      phone: values.phone,
      companyName: diagnosis.companyName ?? undefined,
      diagnosisId: diagnosis.id,
      message: values.message,
    });
    track('lead_submit', { diagnosisId: diagnosis.id });
    message.success('预约成功，顾问将尽快与您联系');
    setLeadOpen(false);
  };

  const handleShare = async () => {
    try {
      const res = await createShare(diagnosis.id);
      setShareUrl(`${window.location.origin}${res.shareUrl}`);
      track('share', { diagnosisId: diagnosis.id });
      setShareOpen(true);
    } catch {
      message.error('生成分享链接失败');
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadPdf(diagnosis.id);
      track('pdf_download', { diagnosisId: diagnosis.id });
      saveBlob(blob, `${diagnosis.companyName || '企业'}-数字化健康度体检报告-${new Date().toISOString().slice(0, 10)}.pdf`);
      message.success('PDF 已下载');
    } catch {
      message.error('PDF 下载失败，请稍后重试');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div>
      {/* 头部 */}
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16}>
          <Col flex="auto">
            <Typography.Title level={3} style={{ margin: 0 }}>
              {diagnosis.companyName || '企业'}数字化健康度体检报告
            </Typography.Title>
            <Space style={{ marginTop: 8 }}>
              <Typography.Text type="secondary">
                诊断时间：
                {new Date(diagnosis.submittedAt ?? diagnosis.createdAt ?? new Date()).toLocaleString('zh-CN')}
              </Typography.Text>
              <Button size="small" icon={<DownloadOutlined />} loading={pdfLoading} onClick={handleDownloadPdf}>
                下载 PDF
              </Button>
              <Button size="small" icon={<ShareAltOutlined />} onClick={handleShare}>
                分享
              </Button>
            </Space>
          </Col>
          <Col>
            <Badge.Ribbon text={`${score.grade} 级 · ${score.gradeLabel}`} color={GRADE_COLORS[score.grade] ?? 'blue'}>
              <Card size="small">
                <Statistic title="健康总分" value={score.totalScore} suffix="/100" valueStyle={{ color: GRADE_COLORS[score.grade] }} />
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>
      </Card>

      {/* 图表区 */}
      <Row gutter={16}>
        <Col xs={24} md={10}>
          <Card title="五维健康度雷达" style={{ marginBottom: 16 }}>
            <EChart option={radarOption as never} height={300} />
          </Card>
        </Col>
        <Col xs={24} md={14}>
          <Card title="维度得分对比" style={{ marginBottom: 16 }}>
            <EChart option={barOption as never} height={300} />
          </Card>
        </Col>
      </Row>

      {/* 等级仪表 + 核心发现 */}
      <Row gutter={16}>
        <Col xs={24} md={10}>
          <Card title="健康等级" style={{ marginBottom: 16 }}>
            <EChart option={gaugeOption as never} height={260} />
          </Card>
        </Col>
        <Col xs={24} md={14}>
          <Card title="核心发现" style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ color: '#52c41a' }}>
              优势领域
            </Typography.Title>
            <List
              size="small"
              dataSource={highlights.strengths}
              renderItem={(d) => (
                <List.Item>
                  <Space>
                    <Tag color="green">{d.name}</Tag>
                    <span>{d.score} 分</span>
                  </Space>
                </List.Item>
              )}
            />
            <Typography.Title level={5} style={{ color: '#f5222d', marginTop: 12 }}>
              薄弱环节
            </Typography.Title>
            <List
              size="small"
              dataSource={highlights.weaknesses}
              renderItem={(d) => (
                <List.Item>
                  <Space>
                    <Tag color="red">{d.name}</Tag>
                    <span>{d.score} 分</span>
                  </Space>
                </List.Item>
              )}
            />
            {score.excludedNa.length > 0 && (
              <Alert
                style={{ marginTop: 12 }}
                type="info"
                showIcon
                message={`${score.excludedNa.length} 题因"不适用"未计分：${score.excludedNa.join(', ')}`}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 改进建议（按模块总结） */}
      <Card title="改进建议" style={{ marginBottom: 16 }}>
        {data.aiSuggestions ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 10, padding: '12px 16px' }}>
              <Typography.Text strong style={{ color: '#531dab' }}>总体结论</Typography.Text>
              <div style={{ fontSize: 14, lineHeight: 1.8, marginTop: 4 }}>{data.aiSuggestions.overview}</div>
            </div>
            <div style={{ background: '#fffbe6', border: '1px solid #ffd666', borderRadius: 10, padding: '12px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography.Text strong style={{ color: '#d46b08' }}>🎯 最优先做的 3 件事</Typography.Text>
                <Tag color="gold">按此顺序执行</Tag>
              </div>
              {data.aiSuggestions.top3.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 13, lineHeight: 1.6 }}>
                  <span
                    style={{
                      background: '#d46b08',
                      color: '#fff',
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span>{t}</span>
                </div>
              ))}
            </div>
            <Typography.Text strong style={{ fontSize: 14 }}>完整建议</Typography.Text>
            {data.aiSuggestions.priorities.map((p, i) => {
              const tm = p.timeline ? (p.timeline.match(/\d+/) ? parseInt(p.timeline.match(/\d+/)![0], 10) : 99) : 99;
              const tColor = tm <= 3 ? '#f5222d' : tm <= 6 ? '#fa8c16' : '#52c41a';
              const mIssues = score.moduleSummaries.find((mm) => mm.name === p.module)?.issues ?? [];
              return (
                <div
                  key={p.module}
                  style={{
                    borderLeft: `4px solid ${tColor}`,
                    borderTop: '1px solid #f0f0f0',
                    borderRight: '1px solid #f0f0f0',
                    borderBottom: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: '10px 14px',
                    background: '#fff',
                  }}
                >
                  <div style={{ fontSize: 13.5, lineHeight: 1.7 }}>
                    <b>建议 {i + 1}｜{p.module}：</b>
                    {p.suggestion}
                  </div>
                  {p.benefit ? (
                    <div style={{ fontSize: 12.5, color: '#389e0d', marginTop: 4, lineHeight: 1.6 }}>💡 预期收益：{p.benefit}</div>
                  ) : null}
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    {p.timeline ? (
                      <span style={{ fontSize: 11, color: tColor, background: '#fff1f0', padding: '1px 8px', borderRadius: 10 }}>{p.timeline}</span>
                    ) : null}
                    {mIssues.length > 0 ? (
                      <span style={{ fontSize: 11, color: '#8c8c8c', background: '#fafafa', padding: '1px 8px', borderRadius: 10 }}>
                        依据：{mIssues.map((it) => (it.questionNumber != null ? `第${it.questionNumber}题` : it.indicator)).join('·')}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <Collapse
              ghost
              size="small"
              items={[
                {
                  key: 'detail',
                  label: '查看全部短板明细（每题得分）',
                  children: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {score.moduleSummaries.map((m) => (
                        <div key={m.dimension}>
                          <Typography.Text strong style={{ fontSize: 13 }}>
                            {m.name}（{m.score ?? '—'} 分）
                          </Typography.Text>
                          <div style={{ fontSize: 12.5, color: '#595959', lineHeight: 1.8 }}>
                            {m.issues.length > 0
                              ? m.issues
                                  .map((it) => `${it.questionNumber != null ? `第${it.questionNumber}题` : ''}「${it.indicator}」${it.score}分`)
                                  .join('、')
                              : '无明显短板'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>🤖 由 AI 基于诊断数据生成，仅供参考</Typography.Text>
          </div>
        ) : (
          score.moduleSummaries.length === 0 ? (
            <Typography.Text type="secondary">恭喜！当前没有触发改进建议，继续保持</Typography.Text>
          ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {score.moduleSummaries.map((m) => (
              <div
                key={m.dimension}
                style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '14px 16px', background: '#fafafa' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Typography.Text strong style={{ fontSize: 15 }}>
                    {m.name}
                  </Typography.Text>
                  {m.score !== null && (
                    <Tag color={m.score < 50 ? 'red' : m.score < 70 ? 'orange' : 'green'}>{m.score} 分</Tag>
                  )}
                </div>
                {m.issues.length > 0 && (
                  <div
                    style={{
                      background: '#fff7f0',
                      borderRadius: 8,
                      padding: '8px 12px',
                      marginBottom: 8,
                      fontSize: 13,
                      lineHeight: 1.7,
                    }}
                  >
                    <b>问题所在：</b>
                    {m.issues.map((it) => `${it.indicator}（${it.score} 分）`).join('、')}
                    ，是「{m.name}」的主要短板，优先改进见效最快。
                  </div>
                )}
                {m.suggestions.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {m.suggestions.map((s, i) => {
                      const p = PRIORITY_LABEL[s.priority];
                      return (
                        <div
                          key={`${s.questionCode}-${i}`}
                          style={{
                            border: '1px solid #ffd6e7',
                            background: '#fff0f6',
                            borderRadius: 8,
                            padding: '8px 12px',
                            fontSize: 13,
                            lineHeight: 1.7,
                          }}
                        >
                          <Tag color={p.color} style={{ marginRight: 8 }}>
                            {p.text}
                          </Tag>
                          <b>建议 {i + 1}</b>：{s.content}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          )
        )}
      </Card>

      {/* 转化区 + 免责 */}
      <Card style={{ marginBottom: 16, background: '#f6ffed' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Typography.Title level={4} style={{ margin: 0 }}>
              想要更深入的诊断与落地方案？
            </Typography.Title>
            <Typography.Text type="secondary">预约资深顾问，30 分钟免费解读报告与改进路径</Typography.Text>
          </Col>
          <Col>
            <Button type="primary" size="large" icon={<PhoneOutlined />} onClick={() => setLeadOpen(true)}>
              预约免费顾问咨询
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="声明">
            本报告基于企业自评，可能存在高估，建议结合顾问访谈校准；5 分与 A 级 = 行业标杆水平。
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal open={shareOpen} title="分享报告" onOk={() => navigator.clipboard.writeText(shareUrl)} onCancel={() => setShareOpen(false)} okText="复制链接">
        <Alert type="info" showIcon message="分享链接 7 天内有效，无需登录即可查看" style={{ marginBottom: 12 }} />
        <Input value={shareUrl} readOnly />
      </Modal>

      <Modal open={leadOpen} title="预约顾问咨询" onOk={submitLead} onCancel={() => setLeadOpen(false)} okText="提交预约">
        <Form form={leadForm} layout="vertical">
          <Form.Item name="name" label="联系人" rules={[{ required: true, message: '请输入联系人' }]}>
            <Input placeholder="您的姓名" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号"
            rules={[{ required: true, pattern: /^1[3-9]\d{9}$/, message: '请输入正确手机号' }]}
          >
            <Input placeholder="用于顾问联系您" maxLength={11} />
          </Form.Item>
          <Form.Item name="message" label="想咨询的问题（选填）">
            <Input.TextArea rows={3} placeholder="如：希望了解数字化转型从哪开始" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function gradeOf(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 50) return 'C';
  if (score >= 30) return 'D';
  return 'E';
}
